import { ApiClientBase } from '@@/api/api';
import { baseApiResponse, RequestBase } from '@@/api/api-common';
import { ApiResult } from '@@/types/ApiResult';
import { ItemRecord } from './api-items';
import {
  BaseSchema,
  CreateEntityPayload,
  DefaultValues,
  GetSuggestionsPayload,
  Permissions,
  UpdateEntityPayload
} from '../schemas/entity';
import { ZodError } from 'zod';

export interface EntityRecord {
  instanceId: string;
  entityId: string;
  parentId: string;
  name: string;
  path: string;
  depth: number;
  childCount: number;
  description: string;
  schema: BaseSchema;
  defaults: DefaultValues;
  permissions: Permissions;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

export interface ListEntitiesResponse extends baseApiResponse {
  entities: EntityRecord[];
}

export interface CreateEntityResponse extends baseApiResponse {
  entity: EntityRecord;
}

interface UpdateEntityResponse extends baseApiResponse {}

interface DeleteEntityBody {
  entityId: string;
}

interface DeleteEntityRequest extends DeleteEntityBody {}

interface DeleteEntityResponse extends baseApiResponse {}

interface GetEntityRequest {
  entityId: string;
}

interface GetEntityResponse extends baseApiResponse {
  entity: EntityRecord;
}

export interface QueryByPathRequest {
  path: string;
}

export interface QueryByPathResponse {
  message: string;
  items: ItemRecord[];
  entities: EntityRecord[];
  entity: EntityRecord | null;
  item: ItemRecord | null;
  path: string;
  evaluated: Record<string, unknown>;
  errorCode?: string;
}

export enum ConnectResourceType {
  CONTACT_FLOW = 'CONTACT_FLOW',
  CONTACT_FLOW_MODULE = 'CONTACT_FLOW_MODULE',
  PROMPT = 'PROMPT',
  PHONE_NUMBER = 'PHONE_NUMBER',
  QUEUE = 'QUEUE'
}

export interface ConnectResource {
  name: string;
  arn: string;
}

export interface GetSuggestionsResponse {
  type: ConnectResourceType;
  resources: ConnectResource[];
}

export class EntitiesClient extends ApiClientBase {
  async listEntities({
    queryString
  }: RequestBase<
    Record<string, never>,
    {
      parentId: string;
    }
  >): Promise<ApiResult<ListEntitiesResponse>> {
    try {
      const headers = await this.getHeaders();
      const queryParams = this.formatQueryStrings(queryString);

      const result = await fetch(this.getDfcApiUrl(`/dfc/entity?${queryParams}`), {
        method: 'GET',
        headers
      });

      if (!result.ok) {
        return this.error('Failed to get Entities');
      }

      return await result.json();
    } catch (error) {
      console.error(error);
      return this.error('Failed to list Entity');
    }
  }

  async getEntity({ input, queryString }: RequestBase<GetEntityRequest>): Promise<ApiResult<GetEntityResponse>> {
    try {
      const headers = await this.getHeaders();
      const queryParams = this.formatQueryStrings(queryString);

      const result = await fetch(this.getDfcApiUrl(`/dfc/entity/${input.entityId}?${queryParams}`), {
        method: 'GET',
        headers
      });

      if (!result.ok) {
        return this.error('Failed to get Entity');
      }

      return await result.json();
    } catch (error) {
      console.error(error);
      return this.error('Failed to get Entity');
    }
  }

  async createEntity({
    input,
    queryString
  }: RequestBase<CreateEntityPayload>): Promise<ApiResult<CreateEntityResponse>> {
    const headers = await this.getHeaders();
    const queryParams = this.formatQueryStrings(queryString);

    const result = await fetch(this.getDfcApiUrl(`/dfc/entity?${queryParams}`), {
      method: 'POST',
      headers,
      body: JSON.stringify({
        name: input.name,
        parentId: input.parentId,
        description: input.description
      })
    });

    if (!result.ok) {
      const jsonResult = await result.json();
      return this.error(jsonResult.data?.message);
    }

    return await result.json();
  }

  async updateEntity(
    entityId: string,
    { input, queryString }: RequestBase<UpdateEntityPayload>
  ): Promise<ApiResult<UpdateEntityResponse>> {
    try {
      const headers = await this.getHeaders();
      const queryParams = this.formatQueryStrings(queryString);

      const result = await fetch(this.getDfcApiUrl(`/dfc/entity/${entityId}?${queryParams}`), {
        method: 'PUT',
        headers,
        body: JSON.stringify(input)
      });

      if (!result.ok) {
        const jsonResult = await result.json();

        if (jsonResult.errors && jsonResult.errors.length > 0) {
          return this.error(jsonResult.errors.map((err: ZodError) => err.message).join(', '));
        }

        return this.error('Failed to update Entity.');
      }

      return await result.json();
    } catch (error) {
      console.error(error);
      return this.error('Failed to update Entity.');
    }
  }

  async deleteEntity({
    input,
    queryString
  }: RequestBase<DeleteEntityRequest>): Promise<ApiResult<DeleteEntityResponse>> {
    try {
      const headers = await this.getHeaders();
      const queryParams = this.formatQueryStrings(queryString);

      const result = await fetch(this.getDfcApiUrl(`/dfc/entity/${input.entityId}?${queryParams}`), {
        method: 'DELETE',
        headers
      });

      if (!result.ok) {
        const jsonResult = await result.json();
        return this.error(jsonResult.data?.message || 'Failed to delete entity');
      }

      return await result.json();
    } catch (error) {
      console.error(error);
      return this.error('Failed to delete Entity');
    }
  }

  async queryByPath({ input, queryString }: RequestBase<QueryByPathRequest>): Promise<ApiResult<QueryByPathResponse>> {
    try {
      const headers = await this.getHeaders();
      const queryParams = this.formatQueryStrings(queryString);

      const result = await fetch(this.getDfcApiUrl(`/dfc/by-path?${queryParams}`), {
        method: 'POST',
        headers,
        body: JSON.stringify(input)
      });

      if (!result.ok) {
        return this.error('Failed to query by path');
      }

      return await result.json();
    } catch (error) {
      console.error(error);
      return this.error('Failed to query by path');
    }
  }

  async getSuggestions({
    input,
    queryString
  }: RequestBase<GetSuggestionsPayload>): Promise<ApiResult<GetSuggestionsResponse>> {
    try {
      const headers = await this.getHeaders();
      const queryParams = this.formatQueryStrings(queryString);

      const result = await fetch(this.getDfcApiUrl(`/dfc/suggestions?${queryParams}`), {
        method: 'POST',
        headers,
        body: JSON.stringify(input)
      });

      if (!result.ok) {
        return this.error('Failed to get suggestions');
      }

      return await result.json();
    } catch (error) {
      console.error(error);
      return this.error('Failed to get suggestions');
    }
  }
}
